import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Menu, X } from 'lucide-react'
import type { SharedPageProps } from '@/types/shared'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

function linkClass(active: boolean) {
  return active
    ? 'font-sans text-[14px] font-medium text-foreground'
    : 'font-sans text-[14px] text-muted-foreground transition-colors hover:text-foreground'
}

function initialsFor(name?: string, companyName?: string) {
  if (name) {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join('')
  }

  return companyName?.[0]?.toUpperCase() ?? '?'
}

export function CompanyHeader() {
  const page = usePage<SharedPageProps>()
  const { currentCompanyMember, currentCompany } = page.props
  const url = page.url
  const [menuOpen, setMenuOpen] = useState(false)

  const funnelActive = url.startsWith('/company/funnel')
  const exploreActive = url.startsWith('/search')

  return (
    <header className="relative flex h-16 items-center justify-between border-b border-border px-5 sm:px-9">
      <div className="flex items-center gap-6">
        <Link href="/company" className="flex items-center gap-2">
          <HyrinWordmark />
        </Link>
        <nav className="hidden items-center gap-5 md:flex">
          <Link href="/company/funnel" className={linkClass(funnelActive)}>
            Funnel
          </Link>
          <Link href="/search" className={linkClass(exploreActive)}>
            Explore candidates
          </Link>
        </nav>
      </div>
      <div className="flex items-center gap-3 sm:gap-5">
        {currentCompanyMember && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Account menu"
                className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {currentCompanyMember.avatarUrl ? (
                  <img
                    src={currentCompanyMember.avatarUrl}
                    alt={currentCompanyMember.name ?? 'Your avatar'}
                    className="size-[30px] rounded-full object-cover"
                  />
                ) : (
                  <div className="flex size-[30px] items-center justify-center rounded-full bg-primary font-sans text-[12px] font-bold text-primary-foreground">
                    {initialsFor(currentCompanyMember.name, currentCompany?.name)}
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-sans text-[14px] text-foreground">
                {currentCompanyMember.name && <div>{currentCompanyMember.name}</div>}
                {currentCompany && (
                  <div className="font-mono text-[12px] font-normal text-muted-foreground">
                    {currentCompany.name}
                  </div>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="font-sans text-[14px]"
                onSelect={() => router.delete('/sign-out')}
              >
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          className="flex size-9 items-center justify-center rounded-[8px] border border-border text-foreground md:hidden"
        >
          {menuOpen ? <X className="size-[18px]" /> : <Menu className="size-[18px]" />}
        </button>
      </div>
      {menuOpen && (
        <nav className="absolute inset-x-0 top-16 z-20 flex flex-col border-b border-border bg-background px-5 py-3 md:hidden">
          <Link
            href="/company/funnel"
            className="py-3 font-sans text-[14px] text-muted-foreground"
            onClick={() => setMenuOpen(false)}
          >
            Funnel
          </Link>
          <Link
            href="/search"
            className="py-3 font-sans text-[14px] text-muted-foreground"
            onClick={() => setMenuOpen(false)}
          >
            Explore candidates
          </Link>
        </nav>
      )}
    </header>
  )
}
